/**
 * Hook for Adobe Font (Typekit) value state and handlers in the font family field.
 * Keeps all Adobe-specific logic out of the main Google-fonts-oriented component.
 */
import { useState, useEffect, useMemo } from "react";
import {
  isAdobeFontSpec,
  parseAdobeFontSpec,
  formatAdobeFontValue,
} from "@shared/modules/branding/utils/adobe-fonts";

export const CUSTOM_ADOBE_FONT_LABEL = "Custom Adobe Font";
export const CUSTOM_CATEGORY = "Custom";

export interface UseAdobeFontFieldResult {
  isAdobeSelected: boolean;
  adobeParsed: { projectId: string; fontId: string } | null;
  adobeProjectId: string;
  adobeFontId: string;
  handleAdobeProjectIdChange: (nextProjectId: string) => void;
  handleAdobeFontIdChange: (nextFontId: string) => void;
  displayValue: string;
  showAdobeInputs: boolean;
  /** Call when user selects the "Custom Adobe Font" option in the list */
  selectAdobeFont: () => void;
  /** Whether the given font name is the custom Adobe option (for selection state) */
  isCustomAdobeOption: (fontName: string) => boolean;
  /** Whether to show custom font preview (Adobe has no preview in picker) */
  shouldHideFontPreview: (fontName: string) => boolean;
}

export function useAdobeFontField(
  value: string,
  onChange: (value: string) => void
): UseAdobeFontFieldResult {
  const isAdobeSelected = isAdobeFontSpec(value) || value === "adobe:";
  const adobeParsed = useMemo(
    () => (value.startsWith("adobe:") ? parseAdobeFontSpec(value) : null),
    [value]
  );
  const [adobeProjectId, setAdobeProjectId] = useState(
    adobeParsed?.projectId ?? ""
  );
  const [adobeFontId, setAdobeFontId] = useState(adobeParsed?.fontId ?? "");

  useEffect(() => {
    if (adobeParsed) {
      setAdobeProjectId(adobeParsed.projectId);
      setAdobeFontId(adobeParsed.fontId);
    } else if (!value.startsWith("adobe:")) {
      setAdobeProjectId("");
      setAdobeFontId("");
    }
  }, [value, adobeParsed]);

  const handleAdobeProjectIdChange = (nextProjectId: string): void => {
    setAdobeProjectId(nextProjectId);
    if (nextProjectId.trim() && adobeFontId.trim()) {
      onChange(formatAdobeFontValue(nextProjectId, adobeFontId));
    } else {
      onChange("adobe:");
    }
  };

  const handleAdobeFontIdChange = (nextFontId: string): void => {
    setAdobeFontId(nextFontId);
    if (adobeProjectId.trim() && nextFontId.trim()) {
      onChange(formatAdobeFontValue(adobeProjectId, nextFontId));
    } else {
      onChange("adobe:");
    }
  };

  const displayValue = ((): string => {
    if (isAdobeSelected) {
      return adobeParsed
        ? `${CUSTOM_ADOBE_FONT_LABEL} (${adobeParsed.fontId})`
        : CUSTOM_ADOBE_FONT_LABEL;
    }
    return value;
  })();

  const selectAdobeFont = (): void => {
    if (!isAdobeSelected) {
      onChange("adobe:");
    }
  };

  const isCustomAdobeOption = (fontName: string): boolean =>
    fontName === CUSTOM_ADOBE_FONT_LABEL;

  const shouldHideFontPreview = (fontName: string): boolean =>
    fontName === CUSTOM_ADOBE_FONT_LABEL;

  return {
    isAdobeSelected,
    adobeParsed,
    adobeProjectId,
    adobeFontId,
    handleAdobeProjectIdChange,
    handleAdobeFontIdChange,
    displayValue,
    showAdobeInputs: value.startsWith("adobe:"),
    selectAdobeFont,
    isCustomAdobeOption,
    shouldHideFontPreview,
  };
}
